import comprasAnalisisService from '../services/comprasAnalisisService';
import kitService, { type KitStockDto } from '../services/kitService';
import productoService, { type EquivalenciasUmDetalle } from '../services/productoService';
import { resolverStockParaUmLinea } from './umVentaDocumento';

const roundStock = (n: number) => Math.round(n * 1000) / 1000;

export function esTipoKit(tipoArticulo?: string | null): boolean {
  return String(tipoArticulo ?? '').trim().toUpperCase() === 'KIT';
}

export interface StockPorDepositoRow {
  nroUbicacion: number;
  nombreUbicacion: string;
  cantidadDeposito: number;
  cantidadAsignada: number;
  disponibleDeposito: number;
  stockProduccion: number;
  stockPendienteOc: number;
  atp: number;
}

export type StockPorDepositoArticuloResult =
  | { kind: 'depositos'; rows: StockPorDepositoRow[] }
  | { kind: 'kit'; kit: KitStockDto };

/** Stock por depósito de un artículo (exacto). KIT → componentes; normal → stock-neto. */
export async function fetchStockPorDepositoArticulo(
  codigoArticulo: string,
  opts?: {
    tipoArticulo?: string | null;
    nroUbicacion?: number | null;
  },
): Promise<StockPorDepositoArticuloResult | null> {
  const codigo = String(codigoArticulo || '').trim();
  if (!codigo) return null;

  let tipo = opts?.tipoArticulo;
  if (tipo == null) {
    try {
      const p = await productoService.getProducto(codigo);
      tipo = p.tipoArticulo;
    } catch {
      tipo = null;
    }
  }

  if (esTipoKit(tipo)) {
    try {
      const kit = await kitService.getStockKit(codigo, opts?.nroUbicacion);
      return { kind: 'kit', kit };
    } catch {
      return null;
    }
  }

  try {
    const comp = await comprasAnalisisService.getStockNetoComposicion(codigo);
    let depositos = comp.depositos ?? [];
    if (opts?.nroUbicacion != null && opts.nroUbicacion > 0) {
      depositos = depositos.filter((d) => d.nroUbicacion === opts.nroUbicacion);
    }
    const rows: StockPorDepositoRow[] = depositos.map((d) => {
      const asignada = Math.max(0, Number(d.stockReservado) || 0);
      const cantidad = Number(d.cantidadActual) || 0;
      const prod = Number(d.stockProduccion) || 0;
      const oc = Number(d.stockPendienteOcRecibir ?? d.stockOrdenCompra) || 0;
      const disponible = Number(d.stockDisponibleVenta);
      return {
        nroUbicacion: d.nroUbicacion,
        nombreUbicacion: d.nombreUbicacion,
        cantidadDeposito: cantidad,
        cantidadAsignada: asignada,
        disponibleDeposito: Number.isFinite(disponible) ? disponible : cantidad - asignada,
        stockProduccion: prod,
        stockPendienteOc: oc,
        atp: Number(d.atp) || cantidad - asignada + prod + oc,
      };
    });
    return { kind: 'depositos', rows };
  } catch {
    return null;
  }
}

/** Aplica equivalencias: stock en UM inventario → UM de la línea. Sin eq, deja el valor. */
export function aplicarStockEnUmLinea(
  stockRaw: number,
  umLinea: string,
  detalle: EquivalenciasUmDetalle | null | undefined,
  producto?: unknown,
): number {
  const r = resolverStockParaUmLinea(stockRaw, umLinea, detalle, producto);
  return roundStock(r.stock);
}

async function fetchStockFisicoTotal(codigoArticulo: string): Promise<number | null> {
  const codigo = String(codigoArticulo || '').trim();
  if (!codigo) return null;
  try {
    const comp = await comprasAnalisisService.getStockNetoComposicion(codigo);
    return Number(comp.totales?.stockDisponibleVenta ?? 0);
  } catch {
    return null;
  }
}

/** Stock disponible del artículo; si es KIT, usa stock ensamblable por componentes. */
export async function fetchStockDisponibleArticulo(
  codigoArticulo: string,
  opts?: {
    tipoArticulo?: string | null;
    nroUbicacion?: number | null;
  },
): Promise<number | null> {
  const codigo = String(codigoArticulo || '').trim();
  if (!codigo) return null;

  let tipo = opts?.tipoArticulo;
  if (tipo == null) {
    try {
      const p = await productoService.getProducto(codigo);
      tipo = p.tipoArticulo;
    } catch {
      tipo = null;
    }
  }

  if (esTipoKit(tipo)) {
    try {
      const kitStock = await kitService.getStockKit(codigo, opts?.nroUbicacion);
      return Number(kitStock.disponible) || 0;
    } catch {
      return 0;
    }
  }

  if (opts?.nroUbicacion != null && opts.nroUbicacion > 0) {
    try {
      const comp = await comprasAnalisisService.getStockNetoComposicion(codigo);
      const dep = (comp.depositos ?? []).find((d) => d.nroUbicacion === opts.nroUbicacion);
      return dep ? Number(dep.stockDisponibleVenta ?? 0) : 0;
    } catch {
      return 0;
    }
  }

  return fetchStockFisicoTotal(codigo);
}

/** Stock disponible total, expresado en la UM de la línea (si hay equivalencia). */
export async function fetchStockDisponibleEnUm(
  codigoArticulo: string,
  umLinea?: string | null,
  detallePrefetch?: EquivalenciasUmDetalle | null,
  producto?: unknown,
  opts?: { tipoArticulo?: string | null; nroUbicacion?: number | null },
): Promise<number | null> {
  const codigo = String(codigoArticulo || '').trim();
  if (!codigo) return null;

  const tipo =
    opts?.tipoArticulo ??
    (producto as { tipoArticulo?: string | null } | undefined)?.tipoArticulo ??
    null;

  const stockRaw = await fetchStockDisponibleArticulo(codigo, {
    tipoArticulo: tipo,
    nroUbicacion: opts?.nroUbicacion,
  });
  if (stockRaw == null) return null;

  const umDoc = String(umLinea ?? '').trim();
  if (!umDoc || esTipoKit(tipo)) return stockRaw;

  let detalle = detallePrefetch ?? null;
  if (!detalle) {
    try {
      detalle = await productoService.getEquivalenciasUmDetalle(codigo);
    } catch {
      return stockRaw;
    }
  }
  return aplicarStockEnUmLinea(stockRaw, umDoc, detalle, producto);
}
